import { useEffect, useState } from "react";
import { Trophy, Radar, Crown } from "lucide-react";
import api from "../lib/api";

export default function LeaderboardTabs({ testId }) {
  const [tab, setTab] = useState("global"); // global | monthly | boss
  const [global, setGlobal] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [boss, setBoss] = useState(null);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/leaderboard").catch(() => ({ data: { items: [] } })),
      api.get("/leaderboard/monthly").catch(() => ({ data: { month: "", items: [] } })),
      api.get("/leaderboard/boss-ranks").catch(() => ({ data: { items: [] } })),
    ]).then(([g, m, b]) => {
      if (cancel) return;
      setGlobal(g.data.items || []);
      setMonthly(m.data);
      setBoss(b.data.items || []);
    });
    return () => {
      cancel = true;
    };
  }, []);

  const items = tab === "global" ? global : tab === "monthly" ? monthly?.items : boss;
  const label = tab === "global"
    ? "All-time"
    : tab === "monthly"
      ? (monthly?.month || "This Month")
      : "Leviathan Slayers";

  return (
    <div className="glass p-6" data-testid={testId || "leaderboard-panel"}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#ffd700]" />
          <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
            Top Pilots
          </div>
        </div>
        <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-white/40 uppercase">
          {label}
        </div>
      </div>

      <div
        className="flex items-center gap-1 mb-4 p-1 border border-white/10 bg-black/40"
        data-testid="leaderboard-tabs"
      >
        <button
          onClick={() => setTab("global")}
          className={`flex-1 py-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
            tab === "global" ? "bg-[#00ffff]/15 text-[#00ffff]" : "text-white/50 hover:text-white"
          }`}
          data-testid="lb-tab-global"
        >
          Global
        </button>
        <button
          onClick={() => setTab("monthly")}
          className={`flex-1 py-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
            tab === "monthly" ? "bg-[#ff007f]/15 text-[#ff007f]" : "text-white/50 hover:text-white"
          }`}
          data-testid="lb-tab-monthly"
        >
          Monthly
        </button>
        <button
          onClick={() => setTab("boss")}
          className={`flex-1 py-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition ${
            tab === "boss" ? "bg-[#ffd700]/15 text-[#ffd700]" : "text-white/50 hover:text-white"
          }`}
          data-testid="lb-tab-boss"
        >
          Boss
        </button>
      </div>

      {items === null || items === undefined ? (
        <div className="flex items-center gap-2 text-white/50 font-mono-cosmic text-xs tracking-widest uppercase">
          <Radar className="w-3.5 h-3.5 animate-spin" /> Syncing...
        </div>
      ) : items.length === 0 ? (
        <div className="text-white/50 font-mono-cosmic text-xs tracking-widest uppercase">
          {tab === "boss" ? "No Leviathan ranks yet — slay every-10/20 bosses to rank up." : "No scores yet."}
        </div>
      ) : (
        <ol className="space-y-2" data-testid="leaderboard-list">
          {items.map((row, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="font-heading font-black w-7 text-lg flex-none"
                  style={{
                    color:
                      i === 0 ? "#ffd700" : i === 1 ? "#00ffff" : i === 2 ? "#ff007f" : "#8b949e",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="truncate text-white/90 text-sm">{row.name}</span>
                {/* Pin badge — the pilot's self-chosen trophy flex. Tier-
                    coloured chip (legendary=purple, gold, silver, bronze).
                    Hidden when no pin is set. Tooltip spells out the
                    achievement name so hovers get the full context. */}
                {row.pin && (() => {
                  const tierColor = {
                    legendary: "#d58bff",
                    gold: "#ffd700",
                    silver: "#d8d8ea",
                    bronze: "#e8a969",
                  }[row.pin.tier] || "#ffd700";
                  return (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 border font-mono-cosmic text-[8px] tracking-[0.15em] uppercase flex-none"
                      style={{
                        color: tierColor,
                        borderColor: `${tierColor}55`,
                        boxShadow: `0 0 5px ${tierColor}30`,
                      }}
                      title={`Pinned trophy · ${row.pin.name}`}
                      data-testid={`lb-pin-${i}`}
                    >
                      <Trophy className="w-2.5 h-2.5" />
                    </span>
                  );
                })()}
              </div>
              {tab === "boss" ? (
                <div className="flex items-center gap-1 font-mono-cosmic text-[10px] tracking-[0.18em] tabular-nums" data-testid={`boss-rank-row-${i}`}>
                  <span className="text-[#ffd700]" title="S-ranks">S {row.s || 0}</span>
                  <span className="text-white/20">·</span>
                  <span className="text-[#7cffb0]" title="A-ranks">A {row.a || 0}</span>
                  <span className="text-white/20">·</span>
                  <span className="text-[#67e8f9]" title="B-ranks">B {row.b || 0}</span>
                  <span className="text-white/20">·</span>
                  <span className="text-[#cbd5e1]" title="C-ranks">C {row.c || 0}</span>
                </div>
              ) : (
                <span className="font-mono-cosmic text-[11px] tracking-widest text-[#00ffff]">
                  {(row.total_score ?? row.score ?? 0).toLocaleString()}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {tab === "monthly" && (
        <div className="mt-3 font-mono-cosmic text-[9px] tracking-[0.3em] text-white/40 uppercase">
          Resets on the 1st · Top 3 unlock Gold Skin · Top 10 unlock Rainbow
        </div>
      )}
      {tab === "boss" && (
        <div className="mt-3 font-mono-cosmic text-[9px] tracking-[0.3em] text-white/40 uppercase flex items-center gap-1">
          <Crown className="w-3 h-3 text-[#ffd700]" /> Sorted by S-rank count · A/B/C break ties
        </div>
      )}
    </div>
  );
}
